import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, FileDown } from "lucide-react";
import SEO from "@/components/SEO";
import { PageHero } from "@/components/site";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  SERVICE_INTERESTS,
  COMPANY_SIZES,
  CONTACT_REASONS,
  TIMING_OPTIONS,
  LEAD_SOURCES,
  PREFERRED_CONTACT,
} from "@/data/content";

const inputCls =
  "w-full rounded-sm border border-brandborder bg-white px-4 py-3 text-ink text-sm outline-none focus:border-brandblue focus:ring-2 focus:ring-brandblue/20 transition-shadow";
const labelCls = "block text-sm font-semibold text-navy mb-2";

const PACKAGE_NAMES = {
  foundation: "Foundation",
  "business-ready": "Business Ready",
  "scale-up-pro": "Scale-Up Pro",
};

export default function Book() {
  const [params] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    company: "",
    job_title: "",
    email: "",
    phone: "",
    company_size: "",
    contact_reason: "",
    prompted: "",
    service_interest: params.get("service") || "",
    package_interest: PACKAGE_NAMES[params.get("package")] || "",
    concern: "",
    success_outcome: "",
    timing: "",
    deadline: "",
    lead_source: "",
    preferred_contact: "",
    lead_channel: "Discovery Call",
    enquiry_type: "Discovery Call",
    source: "website",
    landing_page: typeof window !== "undefined" ? window.location.href : "",
    referrer: typeof document !== "undefined" ? document.referrer : "",
    utm_source: params.get("utm_source") || "",
    utm_medium: params.get("utm_medium") || "",
    utm_campaign: params.get("utm_campaign") || "",
    utm_content: params.get("utm_content") || "",
    privacy_ack: false,
    marketing_opt_in: false,
    website: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const markTouched = (field) =>
    setTouched((current) => ({ ...current, [field]: true }));

  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim());
  const phoneValid =
    !form.phone.trim() ||
    /^[+()\d\s.-]{7,25}$/.test(form.phone.trim());

  const validationErrors = {
    name: !form.name.trim() ? "Please enter your full name." : "",
    company: !form.company.trim() ? "Please enter your company or organisation." : "",
    email: !form.email.trim()
      ? "Please enter your business email address."
      : !emailValid
        ? "Please enter a valid email address, for example name@company.com."
        : "",
    phone: !phoneValid
      ? "Please enter a valid telephone number, or leave this field blank."
      : "",
    lead_source: !form.lead_source
      ? "Please tell us how you heard about TWORAY."
      : "",
    privacy_ack: !form.privacy_ack
      ? "Please accept the Privacy Policy to continue."
      : "",
  };

  const valid = !Object.values(validationErrors).some(Boolean);

  const showError = (field) =>
    (submitAttempted || touched[field]) && validationErrors[field];

  const submit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);

    if (!valid) return;

    setSubmitting(true);
    try {
      const { data } = await api.post("/leads", {
        ...form,
        message: form.concern.trim(),
      });
      setDone(true);
      toast.success(data.message || "Enquiry received.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="page-book">
        <PageHero overline="Discovery Call" title="Thank you — we've got it." />
        <section className="py-24">
          <div className="mx-auto max-w-2xl px-5 sm:px-8 text-center" data-testid="book-success">
            <div className="w-16 h-16 mx-auto rounded-full bg-iceblue grid place-items-center text-brandblue"><Check size={30} /></div>
            <h2 className="mt-6 font-heading font-bold text-3xl text-navy">Your request is in.</h2>
            <p className="mt-4 text-slate text-lg leading-relaxed">
              We'll respond within one business day to arrange your discovery call. You'll then receive our full-price
              brochure and a short discovery questionnaire so our first conversation is as useful as possible.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/brochure" data-testid="success-brochure-link" className="inline-flex items-center justify-center gap-2 bg-navy text-white font-semibold text-sm px-6 py-3 rounded-sm hover:bg-brandblue transition-colors">
                <FileDown size={16} /> View full pricing brochure
              </Link>
              <Link to="/" className="inline-flex items-center justify-center border border-brandborder text-navy font-semibold text-sm px-6 py-3 rounded-sm hover:border-brandblue transition-colors">Back to home</Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="page-book">
      <SEO title="Book a Discovery Call" description="Book a discovery call with TWORAY Ltd — independent technology and cyber security advisory. A short form to help us prepare." />
      <PageHero overline="Book a Discovery Call" title="Tell us a little — we'll take it from there." subtitle="A short form to qualify your enquiry. The full discovery questionnaire comes afterwards." />
      <section className="py-16 sm:py-24">
        <form onSubmit={submit} className="mx-auto max-w-2xl px-5 sm:px-8 space-y-5">
          <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" aria-hidden="true" data-testid="honeypot-field" />
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Full name *</label>
              <input
                className={inputCls}
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                onBlur={() => markTouched("name")}
                aria-invalid={Boolean(showError("name"))}
                data-testid="input-name"
                placeholder="Jane Doe"
              />
              {showError("name") && (
                <p className="mt-1.5 text-xs text-destructive">{validationErrors.name}</p>
              )}
            </div>
            <div>
              <label className={labelCls}>Company *</label>
              <input
                className={inputCls}
                value={form.company}
                onChange={(e) => set("company", e.target.value)}
                onBlur={() => markTouched("company")}
                aria-invalid={Boolean(showError("company"))}
                data-testid="input-company"
                placeholder="Acme Ltd"
              />
              {showError("company") && (
                <p className="mt-1.5 text-xs text-destructive">{validationErrors.company}</p>
              )}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div><label className={labelCls}>Job title</label><input className={inputCls} value={form.job_title} onChange={(e) => set("job_title", e.target.value)} data-testid="input-job-title" placeholder="e.g. Operations Director" /></div>
            <div>
              <label className={labelCls}>Business email *</label>
              <input
                className={inputCls}
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                onBlur={() => markTouched("email")}
                aria-invalid={Boolean(showError("email"))}
                data-testid="input-email"
                placeholder="jane@acme.com"
              />
              {showError("email") && (
                <p className="mt-1.5 text-xs text-destructive">{validationErrors.email}</p>
              )}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Telephone (optional)</label>
              <input
                className={inputCls}
                type="tel"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                onBlur={() => markTouched("phone")}
                aria-invalid={Boolean(showError("phone"))}
                data-testid="input-phone"
                placeholder="+44 …"
              />
              {showError("phone") && (
                <p className="mt-1.5 text-xs text-destructive">{validationErrors.phone}</p>
              )}
            </div>
            <div><label className={labelCls}>Company size</label>
              <select className={inputCls} value={form.company_size} onChange={(e) => set("company_size", e.target.value)} data-testid="select-company-size">
                <option value="">Select…</option>{COMPANY_SIZES.map((s) => <option key={s} value={s}>{s} people</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>What prompted you to contact TWORAY?</label>
            <select
              className={inputCls}
              value={form.contact_reason}
              onChange={(e) => set("contact_reason", e.target.value)}
              data-testid="select-contact-reason"
            >
              <option value="">Select…</option>
              {CONTACT_REASONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelCls}>Tell us a little more</label>
            <textarea
              className={`${inputCls} min-h-[80px]`}
              value={form.prompted}
              onChange={(e) => set("prompted", e.target.value)}
              data-testid="input-prompted"
              placeholder="Optional context about what has prompted the enquiry"
            />
          </div>

          <div>
            <label className={labelCls}>What would you most like help with?</label>
            <select
              className={inputCls}
              value={form.service_interest}
              onChange={(e) => set("service_interest", e.target.value)}
              data-testid="select-service"
            >
              <option value="">Select…</option>
              {SERVICE_INTERESTS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelCls}>Briefly describe the challenge</label>
            <textarea
              className={`${inputCls} min-h-[90px]`}
              value={form.concern}
              onChange={(e) => set("concern", e.target.value)}
              data-testid="input-concern"
              placeholder="A short description is enough"
            />
          </div>

          <div>
            <label className={labelCls}>
              What would a successful outcome look like for your organisation?
            </label>
            <textarea
              className={`${inputCls} min-h-[80px]`}
              value={form.success_outcome}
              onChange={(e) => set("success_outcome", e.target.value)}
              data-testid="input-success"
            />
          </div>

          <div>
            <label className={labelCls}>When are you looking to address this?</label>
            <select
              className={inputCls}
              value={form.timing}
              onChange={(e) => set("timing", e.target.value)}
              data-testid="select-timing"
            >
              <option value="">Select…</option>
              {TIMING_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelCls}>
              Is there an important deadline or business event?
            </label>
            <input
              className={inputCls}
              value={form.deadline}
              onChange={(e) => set("deadline", e.target.value)}
              data-testid="input-deadline"
              placeholder="e.g. customer due diligence, tender, audit, renewal or board deadline"
            />
          </div>

          <div>
            <label className={labelCls}>How did you hear about TWORAY? *</label>
            <select
              className={inputCls}
              value={form.lead_source}
              onChange={(e) => set("lead_source", e.target.value)}
              onBlur={() => markTouched("lead_source")}
              aria-invalid={Boolean(showError("lead_source"))}
              data-testid="select-lead-source"
            >
              <option value="">Select…</option>
              {LEAD_SOURCES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            {showError("lead_source") && (
              <p className="mt-1.5 text-xs text-destructive">{validationErrors.lead_source}</p>
            )}
          </div>

          <div>
            <label className={labelCls}>Preferred contact method</label>
            <select
              className={inputCls}
              value={form.preferred_contact}
              onChange={(e) => set("preferred_contact", e.target.value)}
              data-testid="select-contact-method"
            >
              <option value="">Select…</option>
              {PREFERRED_CONTACT.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="flex items-start gap-3 cursor-pointer" data-testid="privacy-ack-label">
              <input
                type="checkbox"
                checked={form.privacy_ack}
                onChange={(e) => {
                  set("privacy_ack", e.target.checked);
                  markTouched("privacy_ack");
                }}
                data-testid="checkbox-privacy"
                className="mt-1 w-4 h-4 accent-[#0968F0]"
              />
              <span className="text-sm text-slate">We handle your data per our <a href="/legal/privacy" className="text-brandblue font-semibold underline underline-offset-2">Privacy Policy</a>. *</span>
            </label>
            {showError("privacy_ack") && (
              <p className="mt-1.5 ml-7 text-xs text-destructive">{validationErrors.privacy_ack}</p>
            )}
          </div>
          <label className="flex items-start gap-3 cursor-pointer" data-testid="marketing-optin-label">
            <input type="checkbox" checked={form.marketing_opt_in} onChange={(e) => set("marketing_opt_in", e.target.checked)} data-testid="checkbox-marketing" className="mt-1 w-4 h-4 accent-[#0968F0]" />
            <span className="text-sm text-slate">Optional: I'd like occasional insights from TWORAY (you can unsubscribe anytime).</span>
          </label>
          <motion.button whileTap={{ scale: 0.99 }} type="submit" disabled={submitting} data-testid="book-submit-btn"
            className="inline-flex items-center justify-center gap-2 bg-navy text-white font-semibold text-sm px-7 py-3 rounded-sm hover:bg-brandblue transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            {submitting ? "Sending…" : "Book a Discovery Call"} <Check size={16} />
          </motion.button>
        </form>
      </section>
    </div>
  );
}
